import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { GoogleMap, Marker, Circle } from "@react-google-maps/api";
import mapStyleMinimap from "../MapsGoogle/mapStyleMinimap";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Button, LinearProgress, Tooltip } from "@mui/material";

// Estilo contenedor
const mapContainerStyle = { height: "350px", width: "450px" };

// Opciones del mapa (estables)
const mapOptions = {
  styles: mapStyleMinimap,
  disableDefaultUI: false,
  zoomControl: true,
  zoom: 15,
};

// Normaliza distintos formatos de GeoPoint -> {lat, lng} | null
const toLatLng = (geo) => {
  if (!geo) return null;

  if (typeof geo?.latitude === "number" && typeof geo?.longitude === "number") {
    return { lat: geo.latitude, lng: geo.longitude };
  }
  if (typeof geo?._lat === "number" && typeof geo?._long === "number") {
    return { lat: geo._lat, lng: geo._long };
  }
  if (typeof geo?.lat === "number" && typeof geo?.lng === "number") {
    return { lat: geo.lat, lng: geo.lng };
  }

  const lat = parseFloat(geo?.lat ?? geo?.latitude ?? geo?._lat ?? Number.NaN);
  const lng = parseFloat(
    geo?.lng ?? geo?.longitude ?? geo?._long ?? Number.NaN
  );
  if (!isNaN(lat) && !isNaN(lng)) return { lat, lng };

  return null;
};

// Key estable por posición
const keyFromPos = (p) =>
  p ? `${p.lat.toFixed(6)},${p.lng.toFixed(6)}` : "null";

// Hook: crea íconos solo si google.maps está listo
const useMarkerIcons = () => {
  const g =
    (typeof window !== "undefined" && window.google && window.google.maps) ||
    null;

  const homeIcon = useMemo(() => {
    if (!g) return null;
    return {
      url: "/static/images/casaDelivery.png",
      scaledSize: new g.Size(45, 45),
      anchor: new g.Point(22.5, 22.5),
    };
  }, [g]);

  const riderIcon = useMemo(() => {
    if (!g) return null;
    return {
      url: "/static/images/conductor.png",
      scaledSize: new g.Size(45, 45),
      anchor: new g.Point(22.5, 22.5),
    };
  }, [g]);

  const deliveryIcon = useMemo(() => {
    if (!g) return null;
    return {
      url: "/static/images/delivery-man.png",
      scaledSize: new g.Size(35, 35),
      anchor: new g.Point(17.5, 17.5),
    };
  }, [g]);

  return { homeIcon, riderIcon, deliveryIcon, googleMapsReady: !!g };
};

export default function MapDeliveryPosition({
  markersPending,
  riderDestails,
  dispachtHistory,
  isLoaded, // <- pásalo desde el contenedor que hace useLoadScript
}) {
  // 1) <-- TODOS los hooks van SIEMPRE arriba, sin returns antes
  const { homeIcon, riderIcon, deliveryIcon, googleMapsReady } =
    useMarkerIcons();

  const [markerPosition, setMarkerPosition] = useState(null);
  const [markerRider, setMarkerRider] = useState(null);
  const [markerDelivery, setMarkerDelivery] = useState(null);
  const [exceptionGeoRef, setExceptionGeoRef] = useState(null);
  const [mapCenter, setMapCenter] = useState(null);
  const [tooltip, setTooltip] = useState("Haz clic para copiar");

  const mapRef = useRef(null);

  // Destino y excepción
  useEffect(() => {
    const toPos = toLatLng(markersPending?.toAddressLocation);
    if (toPos) {
      setMarkerPosition(toPos);
      setMapCenter((prev) => prev ?? toPos);
    } else if (markersPending?.toAddressLocation) {
      console.error(
        "Coordenadas inválidas para toAddressLocation:",
        markersPending?.toAddressLocation
      );
    }

    const exPos = toLatLng(markersPending?.exceptionGeoRef);
    if (exPos) {
      setExceptionGeoRef(exPos);
    } else if (markersPending?.exceptionGeoRef) {
      console.error(
        "Coordenadas inválidas para exceptionGeoRef:",
        markersPending?.exceptionGeoRef
      );
    }
  }, [markersPending]);

  // Rider online
  useEffect(() => {
    if (riderDestails?.online && riderDestails?.driverLocation) {
      const rPos = toLatLng(riderDestails.driverLocation);
      if (rPos) setMarkerRider(rPos);
      else
        console.error(
          "driverLocation inválido:",
          riderDestails?.driverLocation
        );
    } else {
      setMarkerRider(null);
    }
  }, [riderDestails]);

  // Punto de entrega desde historial (status "99")
  useEffect(() => {
    if (!Array.isArray(dispachtHistory)) {
      console.error("⚠️ dispachtHistory no es un array:", dispachtHistory);
      return;
    }
    const success = dispachtHistory.find((i) => String(i?.status) === "99");
    if (success?.eventGps) {
      const dPos = toLatLng({
        lat: success.eventGps.latitude,
        lng: success.eventGps.longitude,
      });
      if (dPos) setMarkerDelivery(dPos);
      else console.error("eventGps inválido:", success?.eventGps);
    } else {
      setMarkerDelivery(null);
    }
  }, [dispachtHistory]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const handleCopy = () => {
    if (markerDelivery?.lat && markerDelivery?.lng) {
      const formatted = `${markerDelivery.lat}\t${markerDelivery.lng}`;
      navigator.clipboard.writeText(formatted);
      setTooltip("¡Copiado!");
      setTimeout(() => setTooltip("Haz clic para copiar"), 2000);
    }
  };

  const fallbackCenter = { lat: -33.446535, lng: -70.6513006 };

  const deliveryCircleOptions = useMemo(
    () => ({
      strokeColor: "#0af079",
      strokeOpacity: 0.8,
      strokeWeight: 0.5,
      fillColor: "#187d49",
      fillOpacity: 0.08,
      clickable: false,
      draggable: false,
      editable: false,
      visible: true,
      radius: 300,
      zIndex: 1,
    }),
    []
  );

  // 2) <-- En el return, decides qué renderizar según isLoaded (sin cortar hooks)
  return (
    <div>
      {!isLoaded ? (
        <LinearProgress />
      ) : (
        <>
          <div>
            {markerDelivery && (
              <Tooltip title={tooltip} arrow>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCopy}
                  endIcon={<ContentCopyIcon />}
                >
                  lat: {markerDelivery.lat} &nbsp; lng: {markerDelivery.lng}
                </Button>
              </Tooltip>
            )}
          </div>

          <div className="mapControlStyle">
            <GoogleMap
              id="mapControl2"
              mapContainerStyle={mapContainerStyle}
              zoom={15}
              center={mapCenter || fallbackCenter}
              options={mapOptions}
              onLoad={onMapLoad}
            >
              {/* Destino */}
              {markerPosition && (
                <Marker
                  key={`home-${keyFromPos(markerPosition)}`}
                  position={markerPosition}
                  icon={googleMapsReady ? homeIcon || undefined : undefined}
                />
              )}

              {/* Rider */}
              {markerRider && (
                <Marker
                  key={`rider-${keyFromPos(markerRider)}`}
                  position={markerRider}
                  icon={googleMapsReady ? riderIcon || undefined : undefined}
                />
              )}

              {/* Excepción */}
              {exceptionGeoRef && (
                <Marker
                  key={`ex-${keyFromPos(exceptionGeoRef)}`}
                  position={exceptionGeoRef}
                  label={{ text: "Excepción GeoRef", color: "#333" }}
                />
              )}

              {/* Entrega */}
              {markerDelivery && (
                <>
                  <Circle
                    key={`circle-${keyFromPos(markerDelivery)}`}
                    center={markerDelivery}
                    options={deliveryCircleOptions}
                  />
                  <Marker
                    key={`delivery-${keyFromPos(markerDelivery)}`}
                    position={markerDelivery}
                    icon={
                      googleMapsReady ? deliveryIcon || undefined : undefined
                    }
                  />
                </>
              )}
            </GoogleMap>
          </div>
        </>
      )}
    </div>
  );
}
